import { ArrowUpRight, type LucideIcon } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/cn'

export interface StatCardProps {
  label: string
  value: string | number
  icon?: LucideIcon
  delta?: { value: string; positive?: boolean }
  hint?: string
  className?: string
  /**
   * "hero" renders the dark-emerald Donezo-style featured tile (white text,
   * primary-strong background). Use for the lead stat on a dashboard.
   * "default" is the white card variant.
   */
  tone?: 'default' | 'hero'
  onClick?: () => void
  selected?: boolean
}

export function StatCard({
  label,
  value,
  icon: Icon,
  delta,
  hint,
  className,
  tone = 'default',
  onClick,
  selected = false,
}: StatCardProps) {
  const isHero = tone === 'hero'
  const interactive = typeof onClick === 'function'

  return (
    <Card
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-pressed={interactive ? selected : undefined}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick?.()
              }
            }
          : undefined
      }
      className={cn(
        'group relative overflow-hidden p-6',
        isHero && 'bg-[hsl(var(--bk-primary-strong))] text-primary-foreground shadow-none',
        interactive &&
          'cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        selected && 'ring-2 ring-primary border-primary',
        className,
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <p
          className={cn(
            'text-sm font-medium',
            isHero ? 'text-primary-foreground/85' : 'text-muted-foreground',
          )}
        >
          {label}
        </p>
        <span
          className={cn(
            'inline-flex size-7 items-center justify-center rounded-pill transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5',
            isHero
              ? 'bg-white/15 text-primary-foreground'
              : 'bg-accent text-accent-foreground',
          )}
          aria-hidden
        >
          {Icon ? <Icon className="size-3.5" /> : <ArrowUpRight className="size-3.5" />}
        </span>
      </div>
      <p
        className={cn(
          'mt-6 font-semibold leading-none tracking-tight',
          isHero ? 'text-4xl text-primary-foreground' : 'text-3xl text-foreground',
        )}
      >
        {value}
      </p>
      <div className="mt-4 flex items-center gap-2">
        {delta ? (
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded-pill px-2 py-0.5 text-xs font-medium',
              isHero
                ? 'bg-white/15 text-primary-foreground'
                : delta.positive
                  ? 'bg-accent text-accent-foreground'
                  : 'bg-destructive/10 text-destructive',
            )}
          >
            {delta.value}
          </span>
        ) : null}
        {hint ? (
          <span
            className={cn(
              'text-xs',
              isHero ? 'text-primary-foreground/75' : 'text-muted-foreground',
            )}
          >
            {hint}
          </span>
        ) : null}
      </div>
    </Card>
  )
}
